/**
 * partnerSession — client-side state for an institute opened from a
 * distributor's dashboard (advanced distribution).
 *
 * The distributor's staff open an institute their company created, as its
 * owner, who has no password (vc_be utils/managedOwner). The dashboard and
 * the institute are different origins, so the handoff is a one-time ticket
 * redeemed by PartnerLoginLanding on the institute's origin — see
 * vc_be/services/distributor/partnerLogin.js for the other half.
 *
 * Two storage keys, the same pair as a platform support session:
 *   vc-partner-session      — where to return on sign-out, and whose session it is
 *   vc-partner-session-prev — the person's OWN token/user, saved only when both
 *                             are on one origin (plain localhost in development),
 *                             so signing out hands their own session back.
 *
 * Signing out must never land on this institute's login page: whoever is here
 * has no password for it. It goes back to the distributor's dashboard.
 */

const KEY = 'vc-partner-session';
const PREV_KEY = 'vc-partner-session-prev';

/** The open partner session's details, or null. */
export function getPartnerSession() {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function isPartnerSession() {
  return !!getPartnerSession();
}

/** Take over this tab as the institute's owner. */
export function beginPartnerSession({ token, user, partnerSession }) {
  const prevToken = localStorage.getItem('token');
  const prevUser = localStorage.getItem('user');
  if (prevToken && prevUser) {
    try {
      localStorage.setItem(PREV_KEY, JSON.stringify({ token: prevToken, user: prevUser }));
    } catch { /* quota — sign-out still works, it just cannot restore */ }
  } else {
    localStorage.removeItem(PREV_KEY);
  }

  localStorage.setItem('token', token);
  localStorage.setItem('user', JSON.stringify(user));
  localStorage.setItem(KEY, JSON.stringify(partnerSession || {}));
  // Whatever was selected here belonged to someone else's session.
  localStorage.removeItem('vc-selected-branch');
  localStorage.removeItem('selectedRole');
  window.dispatchEvent(new Event('vc-auth-change'));
}

/**
 * Clear the partner session locally, handing back the person's own session
 * when it was on this origin.
 *
 * @returns {{ restored: boolean, returnUrl: string|null }}
 */
export function clearPartnerSession() {
  const session = getPartnerSession();
  localStorage.removeItem(KEY);
  localStorage.removeItem('vc-selected-branch');
  localStorage.removeItem('selectedRole');

  let restored = false;
  try {
    const raw = localStorage.getItem(PREV_KEY);
    if (raw) {
      const prev = JSON.parse(raw);
      if (prev?.token && prev?.user) {
        localStorage.setItem('token', prev.token);
        localStorage.setItem('user', prev.user);
        restored = true;
      }
    }
  } catch { /* fall through to a plain sign-out */ }
  localStorage.removeItem(PREV_KEY);

  if (!restored) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }

  window.dispatchEvent(new Event('vc-auth-change'));
  return { restored, returnUrl: session?.returnUrl || null };
}

/** An ordinary login is never a partner session — drop anything left over. */
export function forgetPartnerSession() {
  localStorage.removeItem(KEY);
  localStorage.removeItem(PREV_KEY);
}

/**
 * The signed-in account is the password-less owner of a distributor-created
 * institute — no password to change, no Google account to link.
 */
export function isManagedAccount(user) {
  return user?.provisionedVia === 'distributor';
}
